import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useAccess } from './access';
import { useAuth } from '../hooks/useAuth';
import { LoadingSpinner } from './common/LoadingSpinner';
import { usePlatform } from '../contexts/usePlatform';
import { useNavigation } from '../hooks/useNavigation';
import { UpgradeBanner } from './navigation';
import { SidebarContent } from './SidebarContent';
import { SidebarWrapper } from './SidebarWrapper';

interface SidebarProps {
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  isSidebarCollapsed: boolean;
}

export default function Sidebar({
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  isSidebarCollapsed,
}: SidebarProps) {
  const location = useLocation();
  const { access, isLoading: isAccessLoading } = useAccess();
  const { isAuthenticated } = useAuth();
  const { selectedPlatform } = usePlatform();

  // Helper to check if on subscription page
  const isOnSubscriptionPage = location.pathname === '/subscription';

  // Get navigation items for the selected platform
  const navigation = useNavigation(selectedPlatform);

  // Close mobile menu on navigation
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname, setIsMobileMenuOpen]);

  const hasDashboardV1Access = access?.features?.has('access:dashboard_v1') ?? false;
  const shouldDisplayUpgrade =
    access &&
    hasDashboardV1Access &&
    !isOnSubscriptionPage &&
    access.features.has('billing_subscription') &&
    !access?.permissions.has('billing:ignore_subscription') &&
    !access?.subscription_active;

  // In unauthenticated preview mode, render sidebar without waiting for access
  if (!isAuthenticated && (isAccessLoading || access == null)) {
    return (
      <SidebarWrapper isMobileMenuOpen={isMobileMenuOpen} isSidebarCollapsed={isSidebarCollapsed}>
        <SidebarContent navigation={navigation} />
      </SidebarWrapper>
    );
  }

  if (isAccessLoading || access == null) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Mobile menu overlay */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-gray-600 bg-opacity-75 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - always fixed, animated with transforms */}
      <SidebarWrapper isMobileMenuOpen={isMobileMenuOpen} isSidebarCollapsed={isSidebarCollapsed}>
        {/* Main navigation */}
        <SidebarContent navigation={navigation} />

        {/* Bottom section */}
        <div className="pt-3">
          {/* Premium banner */}
          {shouldDisplayUpgrade && (
            <div className="px-3 py-2 pb-4">
              <UpgradeBanner isSidebarCollapsed={isSidebarCollapsed} />
            </div>
          )}
        </div>
      </SidebarWrapper>
    </>
  );
}
